import React, { useState } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonContent,
  IonCard,
  IonCardContent,
  IonText,
  IonIcon,
  IonModal,
  IonButton,
  useIonViewWillLeave,
} from '@ionic/react';
import { warningOutline, checkmarkOutline } from 'ionicons/icons';
import { useContactPicker } from '@/contexts/ContactPickerContext';
import { useDeviceInfo } from '@/hooks/useDeviceInfo';
import {
  INTERSTITIAL_BREAKPOINT_COMPACT,
  INTERSTITIAL_BREAKPOINTS_COMPACT,
  pxToRem,
} from '@/modules/config';
import SnowballMenu from '@/modules/user/SnowballMenu';

const ContactsPickerSettings: React.FC = () => {
  const { isAvailable, canBeEnabled } = useContactPicker();
  const { appExitInstructions, isIOS } = useDeviceInfo();
  const [isExplainerOpen, setIsExplainerOpen] = useState(false);

  useIonViewWillLeave(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  });

  const handlePickContacts = () => {
    setIsExplainerOpen(true);
  };

  const finalizeButton = (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 22,
        minWidth: 22,
        padding: isIOS ? '0 4px' : '0 8px',
        borderRadius: 6,
        backgroundColor: 'var(--ion-color-primary)',
        color: 'var(--ion-color-primary-contrast)',
        fontSize: pxToRem(13),
        fontWeight: 600,
        lineHeight: 1,
        verticalAlign: 'middle',
      }}
    >
      {isIOS ? (
        <IonIcon icon={checkmarkOutline} style={{ fontSize: pxToRem(16) }} />
      ) : (
        'Done'
      )}
    </span>
  );

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/settings/contacts" />
          </IonButtons>
          <IonTitle>Device Contact Picker</IonTitle>
          <IonButtons slot="end">
            <SnowballMenu />
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <div style={{ padding: '20px' }}>
          <IonCard>
            <IonCardContent>
              {isAvailable ? (
                <>
                  <IonText>
                    <p>
                      Congratulations, you&apos;re set up to use the picker.
                      Look for a button like this in sharing screens:
                    </p>
                  </IonText>
                  <button
                    type="button"
                    onClick={handlePickContacts}
                    aria-label="More info on using the contact picker"
                    style={{
                      border: 0,
                      height: 44,
                      minWidth: 100,
                      borderRadius: 22,
                      backgroundColor: '#fff',
                      boxShadow: '0 14px 36px rgba(0, 0, 0, 0.12)',
                      color: '#111',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      cursor: 'pointer',
                      padding: '0 12px',
                      margin: '12px auto 20px',
                    }}
                  >
                    <span
                      style={{
                        fontSize: pxToRem(17),
                        lineHeight: 1,
                        fontWeight: 500,
                      }}
                    >
                      +
                    </span>
                    <span
                      style={{
                        fontSize: pxToRem(12),
                        lineHeight: pxToRem(13),
                        fontWeight: 600,
                        textAlign: 'left',
                      }}
                    >
                      Device
                      <br />
                      Contact
                    </span>
                  </button>
                </>
              ) : canBeEnabled ? (
                <>
                  <h3>Adjust your settings:</h3>
                  <ol>
                    <li>Switch to System Settings</li>
                    <li>Choose Apps &gt; Safari</li>
                    <li>Scroll down to bottom and tap on Advanced</li>
                    <li>Tap on Features Flags</li>
                    <li>
                      Scroll down past CSS options to enable Contact Picker API
                    </li>
                    {appExitInstructions.steps.map((step, i) => (
                      <li key={i}>{step}</li>
                    ))}
                    <li>Reopen the Snowball app</li>
                  </ol>
                  <div
                    style={{
                      background: '#f4f4f4',
                      borderRadius: '8px',
                      padding: '40px',
                      textAlign: 'center',
                      marginBottom: '20px',
                    }}
                  >
                    <IonText color="medium">
                      <p>[Video: Settings Flow Tutorial]</p>
                    </IonText>
                  </div>
                </>
              ) : (
                <IonText color="warning">
                  <p
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <IonIcon icon={warningOutline} />
                    Your device does not support the Contact Picker API.
                  </p>
                </IonText>
              )}
            </IonCardContent>
          </IonCard>
        </div>
      </IonContent>
      <IonModal
        isOpen={isExplainerOpen}
        initialBreakpoint={INTERSTITIAL_BREAKPOINT_COMPACT}
        breakpoints={INTERSTITIAL_BREAKPOINTS_COMPACT}
        handle
        onDidDismiss={() => setIsExplainerOpen(false)}
      >
        <IonContent className="ion-padding">
          <IonText>
            <p>
              The picker will show all contacts on your device. Tap on the
              contact icon on the left to choose one or more, then tap on the{' '}
              {finalizeButton} button in the top right to finalize your
              selection.
            </p>
          </IonText>
          <IonButton expand="block" onClick={() => setIsExplainerOpen(false)}>
            Got it
          </IonButton>
        </IonContent>
      </IonModal>
    </IonPage>
  );
};

export default ContactsPickerSettings;
